import { useState, useMemo, useCallback } from 'react';
import './ToolStyles.css';
import { validateIdCards, generateIdCards } from '../utils/idCard';
import type { GenerateOptions } from '../utils/idCard';

type Tab = 'validate' | 'generate';

const IdCardTool = () => {
  const [activeTab, setActiveTab] = useState<Tab>('validate');

  // ========== 校验状态 ==========
  const [input, setInput] = useState('');
  const results = useMemo(() => validateIdCards(input), [input]);
  const validCount = results.filter((r) => r.valid).length;
  const invalidCount = results.length - validCount;

  // ========== 生成状态 ==========
  const [minAge, setMinAge] = useState(1);
  const [maxAge, setMaxAge] = useState(120);
  const [gender, setGender] = useState<'不限' | '男' | '女'>('不限');
  const [allowX, setAllowX] = useState(true);
  const [count, setCount] = useState(10);
  const [generatedIds, setGeneratedIds] = useState<string[]>([]);

  const handleGenerate = useCallback(() => {
    const options: GenerateOptions = {
      minAge,
      maxAge,
      allowX,
    };
    if (gender !== '不限') {
      options.gender = gender;
    }
    setGeneratedIds(generateIdCards(count, options));
  }, [minAge, maxAge, gender, allowX, count]);

  const copyAll = () => {
    navigator.clipboard.writeText(generatedIds.join('\n'));
  };

  // ========== 渲染 ==========
  return (
    <div className="tool-container">
      {/* Tab 栏 */}
      <div className="tab-bar">
        <button
          className={`tab-button${activeTab === 'validate' ? ' active' : ''}`}
          onClick={() => setActiveTab('validate')}
        >
          校验
        </button>
        <button
          className={`tab-button${activeTab === 'generate' ? ' active' : ''}`}
          onClick={() => setActiveTab('generate')}
        >
          生成
        </button>
      </div>

      {/* ========== 校验 Tab ========== */}
      {activeTab === 'validate' && (
        <>
          <div className="input-group">
            <label className="input-label">身份证号码</label>
            <textarea
              className="tool-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={6}
              placeholder="每行输入一个18位身份证号..."
            />
          </div>

          {results.length > 0 && (
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
                display: 'flex',
                gap: 16,
              }}
            >
              <span>共 {results.length} 个</span>
              <span style={{ color: 'var(--accent)' }}>✅ {validCount} 合法</span>
              {invalidCount > 0 && (
                <span style={{ color: 'var(--danger)' }}>❌ {invalidCount} 不合法</span>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {results.map((r, idx) => (
              <div
                key={idx}
                style={{
                  padding: '16px 18px',
                  borderRadius: 14,
                  border: `1px solid ${r.valid ? 'rgba(52, 211, 153, 0.3)' : 'rgba(248, 113, 113, 0.3)'}`,
                  backgroundColor: r.valid
                    ? 'rgba(52, 211, 153, 0.06)'
                    : 'rgba(248, 113, 113, 0.06)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontSize: 16 }}>{r.valid ? '✅' : '❌'}</span>
                  <span
                    style={{
                      fontFamily: "'SF Mono', 'Monaco', 'Menlo', 'Consolas', monospace",
                      fontSize: 15,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {r.raw}
                  </span>
                </div>

                {!r.valid && r.errors.length > 0 && (
                  <ul
                    style={{
                      margin: '4px 0 0 28px',
                      padding: 0,
                      listStyle: 'none',
                    }}
                  >
                    {r.errors.map((err, ei) => (
                      <li
                        key={ei}
                        style={{
                          fontSize: 13,
                          color: 'var(--danger)',
                          lineHeight: 1.7,
                        }}
                      >
                        • {err}
                      </li>
                    ))}
                  </ul>
                )}

                {r.valid && r.info && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 24,
                      flexWrap: 'wrap',
                      marginLeft: 28,
                      fontSize: 13,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <span>出生: {r.info.birthDate}</span>
                    <span>年龄: {r.info.age}岁</span>
                    <span>性别: {r.info.gender}</span>
                    <span>地区: {r.info.region}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {!input.trim() && (
            <div className="info-text">输入身份证号码后自动校验，支持多行批量</div>
          )}
        </>
      )}

      {/* ========== 生成 Tab ========== */}
      {activeTab === 'generate' && (
        <>
          {/* 参数设置 */}
          <div className="info-box">
            <h4>生成参数</h4>

            {/* 年龄区间 */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                年龄区间
              </span>
              <input
                className="tool-input"
                type="number"
                min={0}
                max={150}
                value={minAge}
                onChange={(e) => setMinAge(Math.max(0, Math.min(150, Number(e.target.value))))}
                style={{ width: 80, textAlign: 'center' }}
              />
              <span style={{ color: 'var(--text-tertiary)' }}>~</span>
              <input
                className="tool-input"
                type="number"
                min={0}
                max={150}
                value={maxAge}
                onChange={(e) => setMaxAge(Math.max(0, Math.min(150, Number(e.target.value))))}
                style={{ width: 80, textAlign: 'center' }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>岁</span>
            </div>

            {/* 性别 */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                性别
              </span>
              <select
                className="tool-select"
                value={gender}
                onChange={(e) => setGender(e.target.value as '不限' | '男' | '女')}
                style={{ width: 120 }}
              >
                <option value="不限">不限</option>
                <option value="男">男</option>
                <option value="女">女</option>
              </select>
            </div>

            {/* 允许 X 结尾 */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
              <input
                type="checkbox"
                id="allowX"
                checked={allowX}
                onChange={(e) => setAllowX(e.target.checked)}
              />
              <label
                htmlFor="allowX"
                style={{ fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}
              >
                允许末位为 X
              </label>
            </div>

            {/* 生成数量 */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                生成数量
              </span>
              <input
                className="tool-input"
                type="number"
                min={1}
                max={100}
                value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(100, Number(e.target.value))))}
                style={{ width: 80, textAlign: 'center' }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>个</span>
            </div>
          </div>

          {/* 按钮 */}
          <div className="button-group">
            <button className="tool-button primary" onClick={handleGenerate}>
              生成身份证号
            </button>
            {generatedIds.length > 0 && (
              <button className="tool-button" onClick={copyAll}>
                复制全部
              </button>
            )}
          </div>

          {/* 结果列表 */}
          {generatedIds.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {generatedIds.map((id, idx) => {
                const info = validateIdCards(id)[0];
                return (
                  <div
                    key={idx}
                    style={{
                      padding: '16px 18px',
                      borderRadius: 14,
                      border: '1px solid rgba(52, 211, 153, 0.3)',
                      backgroundColor: 'rgba(52, 211, 153, 0.06)',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "'SF Mono', 'Monaco', 'Menlo', 'Consolas', monospace",
                        fontSize: 15,
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        letterSpacing: '0.5px',
                        marginBottom: 8,
                      }}
                    >
                      {id}
                    </div>
                    {info.valid && info.info && (
                      <div
                        style={{
                          display: 'flex',
                          gap: 24,
                          flexWrap: 'wrap',
                          fontSize: 13,
                          color: 'var(--text-secondary)',
                        }}
                      >
                        <span>出生: {info.info.birthDate}</span>
                        <span>年龄: {info.info.age}岁</span>
                        <span>性别: {info.info.gender}</span>
                        <span>地区: {info.info.region}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {generatedIds.length === 0 && (
            <div className="info-text">设置参数后点击「生成身份证号」</div>
          )}
        </>
      )}
    </div>
  );
};

export default IdCardTool;
